import React, { forwardRef, useCallback, useMemo } from "react";
import { Drawer, Box, Grid, Typography, Button, Fab, alpha } from "@mui/material";
import { Camera, Image, Upload, CreateNewFolder, Add } from "@mui/icons-material";
import { createFolder } from "./app/transfer";
import { useUploadEnqueue } from "./app/transferQueue";

interface IconCaptionButtonProps {
  icon: React.ReactNode;
  caption: string;
  onClick: () => void;
}

function IconCaptionButton({ icon, caption, onClick }: IconCaptionButtonProps) {
  return (
    <Button
      color="inherit"
      onClick={onClick}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        py: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: 'rgba(60,64,67,0.2)',
          backgroundColor: '#f8f9fa',
          boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: 'rgba(26, 115, 232, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" fontWeight={400} sx={{ textTransform: "none", color: 'text.primary' }}>
        {caption}
      </Typography>
    </Button>
  );
}

interface UploadFabProps {
  onClick: () => void;
}

export const UploadFab = forwardRef<HTMLButtonElement, UploadFabProps>(
  function UploadFab({ onClick }, ref) {
    return (
      <Fab
        ref={ref}
        aria-label="Upload"
        variant="circular"
        color="primary"
        size="large"
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          backgroundColor: 'primary.main',
          boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'primary.dark',
            boxShadow: '0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)',
          },
        }}
        onClick={onClick}
      >
        <Add sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
      </Fab>
    );
  }
);

interface UploadDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  cwd: string;
  onUpload: () => void;
}

export default function UploadDrawer({
  open,
  setOpen,
  cwd,
  onUpload,
}: UploadDrawerProps) {
  const uploadEnqueue = useUploadEnqueue();

  const handleUpload = useCallback(
    (action: "photo" | "image" | "file") => () => {
      const input = document.createElement("input");
      input.type = "file";

      switch (action) {
        case "photo":
          input.accept = "image/*";
          input.capture = "environment";
          break;
        case "image":
          input.accept = "image/*,video/*";
          break;
        case "file":
          input.accept = "*/*";
          break;
      }

      input.multiple = true;
      input.onchange = async () => {
        if (!input.files) return;
        const files = Array.from(input.files);
        uploadEnqueue(
          ...files.map((file) => ({ file, basedir: cwd }))
        );
        setOpen(false);
        onUpload();
      };
      input.click();
    },
    [cwd, onUpload, setOpen, uploadEnqueue]
  );

  const takePhoto = useMemo(() => handleUpload("photo"), [handleUpload]);
  const uploadImage = useMemo(() => handleUpload("image"), [handleUpload]);
  const uploadFile = useMemo(() => handleUpload("file"), [handleUpload]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: "24px 24px 0 0",
          backgroundColor: 'background.default',
        }
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            backgroundColor: 'divider',
            borderRadius: 2,
            mx: 'auto',
            mb: 3,
          }}
        />
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3, px: 1 }}>
          Add new
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <IconCaptionButton
              icon={<Camera sx={{ fontSize: 28, color: 'primary.main' }} />}
              caption="Camera"
              onClick={takePhoto}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <IconCaptionButton
              icon={<Image sx={{ fontSize: 28, color: 'primary.main' }} />}
              caption="Image/Video"
              onClick={uploadImage}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <IconCaptionButton
              icon={<Upload sx={{ fontSize: 28, color: 'primary.main' }} />}
              caption="Upload File"
              onClick={uploadFile}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <IconCaptionButton
              icon={<CreateNewFolder sx={{ fontSize: 28, color: 'primary.main' }} />}
              caption="New Folder"
              onClick={async () => {
                setOpen(false);
                await createFolder(cwd);
                onUpload();
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}
